import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  MAP_TILE_URL,
  MAP_TILE_ATTRIBUTION,
  MAP_TILE_MAX_NATIVE_ZOOM
} from '../../../config/mapTiles.js';
import { getDataColor } from '../../../styles/dataColors.js';

/**
 * EventsHandler para atrapar eventos del mapa si se requieren
 */
const EventsHandler = ({ onMapClick, onMapMoveEnd, onMapDoubleClick }) => {
  const map = useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
    dblclick: (e) => {
      if (onMapDoubleClick) {
        onMapDoubleClick(e.latlng);
      }
    },
    moveend: () => {
      if (onMapMoveEnd) {
        onMapMoveEnd(map.getCenter());
      }
    }
  });
  return null;
};

/** Normaliza el centro a {lat, lng}: se acepta [lat, lng] u objeto. */
const toLatLng = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return { lat: value[0], lng: value[1] };
  if (typeof value === 'object') return { lat: value.lat, lng: value.lng ?? value.lon };
  return null;
};

/**
 * Recentra el mapa cuando cambia la prop `center`.
 *
 * MapContainer congela `center` al montar: sin esto, la única forma de mover el
 * mapa desde afuera era remontarlo (con `key`), lo que reinicializa Leaflet y
 * vuelve a pedir todos los tiles. Acá se mueve la vista del mapa ya montado.
 *
 * Solo actúa cuando el valor de la coordenada cambia de verdad, no en cada
 * render: así no pisa el paneo manual del usuario.
 */
const CenterSync = ({ center }) => {
  const map = useMap();
  // Arranca con el centro inicial, que MapContainer ya aplicó al montar.
  const appliedCenter = useRef(toLatLng(center));

  useEffect(() => {
    const next = toLatLng(center);
    if (!next || next.lat == null || next.lng == null) return;

    const previous = appliedCenter.current;
    if (previous && previous.lat === next.lat && previous.lng === next.lng) return;

    appliedCenter.current = next;
    // Se conserva el zoom actual: mover el centro no debe deshacer el zoom del usuario.
    map.setView([next.lat, next.lng], map.getZoom());
  }, [center, map]);

  return null;
};

/**
 * Componente genérico para mapas
 * @param {Array|Object} center Centro del mapa [lat, lng] o {lat, lng}. Si cambia,
 *                              el mapa se recentra sin remontarse (ver CenterSync)
 * @param {number} zoom Nivel de zoom inicial
 * @param {number} minZoom Nivel mínimo de zoom
 * @param {number} maxZoom Nivel máximo de zoom
 * @param {Object} maxBounds Límites geográficos del mapa
 * @param {number} maxBoundsViscosity Viscosidad de los límites
 * @param {Object} style Estilos inline para el contenedor
 * @param {string} className Clases CSS adicionales
 * @param {Function} onMapClick Callback para manejar clicks en el mapa
 * @param {Function} onMapDoubleClick Callback para manejar doble click en el mapa
 * @param {string} tileLayerUrl URL de la capa base (default: OpenStreetMap, configurable con VITE_MAP_TILE_URL)
 * @param {string} tileLayerAttribution Atribución del proveedor de tiles
 * @param {number} tileLayerMaxNativeZoom Último zoom con tiles reales del proveedor
 * @param {boolean} attributionControl Muestra el crédito del proveedor (requerido por su licencia)
 * @param {boolean} warmTiles Aplica un tinte cálido (amarillo) sobre la capa base
 * @param {Array} markers Arreglo de marcadores a renderizar
 * @param {React.ReactNode} children Componentes adicionales de Leaflet
 */
const GenericMap = ({
  center,
  zoom = 13,
  zoomControl = true,
  minZoom = 3,
  maxZoom = 18,
  maxBounds,
  maxBoundsViscosity = 1.0,
  style = { height: '400px', width: '100%' },
  className = 'rounded-lg',
  onMapClick,
  onMapDoubleClick,
  onMapMoveEnd,
  tileLayerUrl = MAP_TILE_URL,
  tileLayerAttribution = MAP_TILE_ATTRIBUTION,
  tileLayerMaxNativeZoom = MAP_TILE_MAX_NATIVE_ZOOM,
  markers = [],
  children,
  loading = false,
  attributionControl = true,
  warmTiles = true
}) => {
  const tileToneClass = warmTiles ? 'map-tiles-warm' : '';

  // Un único MapContainer para los dos estados. Si cada rama renderizara el suyo,
  // React desmontaría uno y montaría el otro cada vez que cambia `loading`, y
  // Leaflet reinicializaría el mapa entero volviendo a pedir todos los tiles.
  // El overlay de carga va como hermano por encima: al tapar el mapa completo ya
  // bloquea clicks y rueda, así que tampoco hace falta desactivar los handlers.
  return (
    <div
      className={`relative overflow-hidden ${className || ''}`.trim()}
      style={style}
    >
      <MapContainer
        center={center}
        zoom={zoom}
        zoomControl={zoomControl}
        minZoom={minZoom}
        maxZoom={maxZoom}
        maxBounds={maxBounds}
        maxBoundsViscosity={maxBoundsViscosity}
        style={{ height: '100%', width: '100%' }}
        className={`rounded-lg ${tileToneClass}`.trim()}
        attributionControl={attributionControl}
      >
        <TileLayer
          url={tileLayerUrl}
          attribution={tileLayerAttribution}
          maxNativeZoom={tileLayerMaxNativeZoom}
          // Mantiene cargados los tiles del borde: al desplazar no aparecen huecos grises.
          keepBuffer={4}
          // No pide tiles intermedios mientras dura la animación de zoom.
          updateWhenZooming={false}
        />

        {/* Fuera del bloque de carga: el centro puede cambiar mientras carga. */}
        <CenterSync center={center} />

        {/* Durante la carga se muestra solo la capa base, igual que antes:
            los marcadores y las capas de la pantalla quedan fuera. */}
        {!loading && (
          <>
            {(onMapClick || onMapMoveEnd || onMapDoubleClick) && (
              <EventsHandler
                onMapClick={onMapClick}
                onMapMoveEnd={onMapMoveEnd}
                onMapDoubleClick={onMapDoubleClick}
              />
            )}

            {markers.map((marker, index) => (
              <React.Fragment key={marker.id || index}>
                {marker.circle && (
                  <Circle
                    center={marker.position}
                    radius={marker.circle.radius || 50}
                    pathOptions={{
                      color: marker.circle.color || getDataColor(0),
                      fillColor: marker.circle.fillColor || getDataColor(0),
                      fillOpacity: marker.circle.fillOpacity || 0.2,
                      weight: marker.circle.weight || 3
                    }}
                  />
                )}

                <Marker
                  position={marker.position}
                  {...(marker.icon ? { icon: marker.icon } : {})}
                  interactive={marker.interactive !== undefined ? marker.interactive : true}
                >
                  {marker.popup && (
                    <Popup>
                      {marker.popup}
                    </Popup>
                  )}
                </Marker>
              </React.Fragment>
            ))}

            {children}
          </>
        )}
      </MapContainer>

      {/* Overlay con blur y shimmer (esqueleto) */}
      {loading && (
        <div className="absolute inset-0 z-[1000] backdrop-blur-sm bg-bg/40 rounded-lg overflow-hidden flex items-center justify-center">
          {/* Efecto Shimmer usando un pseudo-elemento animado definido con Tailwind */}
          <div className="absolute inset-0 z-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]"></div>
        </div>
      )}

    </div>
  );
};

export default GenericMap;
